import * as THREE from 'three';
import { scene } from './scene.js';
import { buildBody, layoutBody, showHeld, restHands, onRemoteDeath } from './remote.js';
// ⚠️ Напрямую из `skinbody.js`, а не через `remote.js`: труп ему не игрок,
// своей ветки в `updateRemotes` у него нет — а лишний реэкспорт только
// создал бы вид, будто модель трупа живёт по чужим правилам.
import { syncSkin, dropSkin } from './skinbody.js';
import { bounceAABB } from './grenades.js';
import { R } from './state.js';
import { nearPoint } from './mapgrid.js';

// ============================================================
//  Трупы: тело падает, отскакивает от стен и через 10 секунд тает.
//
//  ⚠️ Всё здесь — ЧИСТО КЛИЕНТСКОЕ, и это не упущение, а суть. Мёртвый на
//  сервере неподвижен: хитбоксов у него нет, урона он не получает, в PVS
//  не участвует. Значит, где именно упало тело, не влияет ни на что, кроме
//  картинки, — и синхронизировать падение между клиентами не нужно вовсе.
//  С сервера приходит ровно одно: откуда прилетело и насколько сильно
//  (хвост кадра OP_KILL, см. proto.rs::KILL_FRAME_LEN).
//
//  Модель падения — «шест из двух точек»: ступни и плечи. Каждая точка
//  падает сама (гравитация + отскок от ящиков), между ними жёсткая связь
//  длиной в рост. Ориентация тела выводится из вектора между точками.
//
//  Почему так, а не рэгдолл из 15 тел на шарнирах: тому нужен физдвижок на
//  клиенте и долгий тюнинг, иначе тело складывается в узел. А почему не одна
//  точка: тогда поворот пришлось бы задавать анимацией по времени, и падение
//  выглядело бы одинаково всегда — у стены, на ящике и посреди поля.
//  Две точки дают опрокидывание БЕСПЛАТНО: импульс в плечи разворачивает
//  тело сам, потому что низ остаётся на месте.
//
//  ⚠️ ВИДНО при этом человеческую модель, а не коробки: тело собирается той же
//  `buildBody`, что и у живого чужого, и кожа надевается на тот же риг
//  (`syncSkin` в конце кадра, см. md/models.md 2.3.7). Коробки никуда не
//  делись — они погашены и работают невидимым ригом, по которому и считается
//  вся физика ниже. Поэтому откат `?body=boxes` действует и на трупы.
// ============================================================

/** Сколько труп лежит, с. ⚠️ Дольше серверного RESPAWN_DELAY (2.5 с) —
 *  решение осознанное: убитый успевает возродиться и бегать рядом со своим
 *  телом. В шутерах так и есть, и путаницы это не создаёт (труп лежит и не
 *  двигается), зато тело успевает увидеть тот, кто прибежал на звук боя. */
const LIFE = 10.0;

/** Последние секунды жизни — растворение. Резко исчезающее тело читается
 *  как «пропало из-за лага», а не «истлело». */
const FADE = 0.9;

/** Предел одновременных тел. Осколочная в толпу ботов даёт шесть трупов
 *  разом, и без потолка редкая перестрелка превращалась бы в свалку.
 *  Лишний снимается самый старый — он и так почти растаял. */
const MAX = 12;

const GRAVITY = 20;          // как у гранат (grenade.rs)
const POINT_R = 0.26;        // радиус точки для отскока от ящиков
const REST_Y = 0.22;         // высота точки, лежащей на полу (полтолщины тела)

// Отскок и трение. Глуше, чем у гранаты (0.35 / 0.6): тело не мяч.
const BOUNCE = 0.18;

// ⚠️ ВСЕ гашения — В СЕКУНДУ, а не на кадр, и это не педантизм.
// Первая версия просто умножала скорость на константу каждый кадр: при 60 к/с
// это 0.86⁶⁰ ≈ 0.0001 за секунду, то есть импульс умирал мгновенно и тело
// всегда падало на месте (жалоба владельца «от гранаты не отлетает»).
// Множитель на кадр ещё и означает, что на 144 Гц тело затормозит вчетверо
// быстрее, чем на 60, — физика начнёт зависеть от частоты кадров.
const AIR_DAMP = 0.25;         // сопротивление воздуха, 1/с
const GROUND_FRICTION = 2.6;   // трение о пол, 1/с — тело скользит и замирает

/** Разовое гашение при ударе о стену или ящик. Вот ЭТО применяется на
 *  событие, а не на время, поэтому множитель здесь уместен: гранатные
 *  коэффициенты внутри `bounceAABB` для тела слишком упруги. */
const HIT_DAMP = 0.55;

/** Как толчок делится между плечами и ступнями.
 *
 *  ⚠️ Тут два требования сразу, и они тянут в разные стороны. РАЗНИЦА долей
 *  опрокидывает тело (дай поровну — труп улетит доской, оставшись стоять),
 *  а ВЕЛИЧИНА нижней доли отвечает за то, отлетает тело или падает на месте.
 *  Первая версия ставила 0.15: ступни оставались приколоченными, жёсткая
 *  связь тут же утягивала плечи обратно, и от гранаты тело просто заваливалось
 *  рядом. Половина импульса в ноги даёт и полёт, и опрокидывание. */
const TOP_SHARE = 1.0, BOTTOM_SHARE = 0.5;

/** Скорость, которую даёт полная сила удара (power = 1), м/с. Главный
 *  регулятор «насколько эффектно отлетает» — крутить здесь. Полную силу
 *  даёт осколочная в упор; хедшот винтовки — примерно две трети от неё. */
const KICK = 12.0;

/** Подброс: сервер шлёт только «откуда прилетело», а насколько тело
 *  подкинуть — решает картинка (см. комментарий у детонации в room.rs).
 *  Доля от силы удара. Без него взрыв возит тела по полу, а не подбрасывает. */
const LIFT = 0.6;

/** Толчок, когда направления нет вовсе (сгорел в молотове). Тело обязано
 *  куда-то завалиться: идеально вертикальный шест стоит в равновесии и сам
 *  не упадёт никогда. Роняем вперёд, куда смотрел — это читается как
 *  «подкосились ноги», а не как случайный пинок. */
const COLLAPSE = 1.6;

/** Плечо считается от уровня глаз — та же величина, что в remote.js. */
const SHOULDER_DROP = 0.16;

/** Уровень глаз стоящего в полный рост — зеркало `eye` для стойки в remote.js
 *  (там `prone ? 0.45 : crouch ? 1.0 : 1.7`). Труп всегда раскладывается по
 *  нему, чтобы вставать из любой позы, см. `spawnCorpse`. */
const STAND_EYE = 1.7;

// ---- Доваливание конечностей (см. `settleLimbs`) ----
//
// ⚠️ Жалоба владельца: «конечности иногда торчат вверх, как будто убитый не
// может забрать ноги». Причина в том, что `limpPose` раскидывает конечности
// ОДИН раз, в момент смерти, и дальше они едут вместе с телом жёстко. Тело
// падает и разворачивается как угодно, а поза остаётся той же — то, что
// в момент смерти было «рука вниз», после переворота смотрит в небо.
//
// Лечится не сужением разброса (тогда пропадёт сам эффект «раскидало»),
// а вторым этапом: тело легло — конечности доваливаются на пол под
// собственным весом. Разброс остаётся, торчащих в небо рук не остаётся.

/** Порог «тело остановилось»: сумма квадратов скоростей обеих точек, (м/с)².
 *  В полёте там 25…150, у лежащего — доли (гравитация против отскока пола). */
const REST_SPEED2 = 2.0;

/** Если тело так и не успокоилось (застряло между ящиками) — роняем
 *  конечности всё равно: висящие в воздухе руки хуже неточной опоры. */
const REST_TIMEOUT = 3.0;

/** Угловое ускорение падающей конечности, рад/с². Не мгновенная укладка:
 *  конечность обязана ПАДАТЬ, иначе видно, как поза «переключилась». */
const LIMB_FALL = 9.0;

/** Предел угловой скорости, рад/с — чтобы длинная дуга не выглядела взмахом. */
const LIMB_MAX_W = 5.0;

/** Скорость распрямления локтя/колена, 1/с (см. `straightenJoint`). */
const LIMB_STRAIGHTEN = 3.0;

/** На сколько ось конечности лежит выше поверхности.
 *
 *  ⚠️ Это полутолщина САМОГО ТОЛСТОГО сегмента (0.09 у кисти, 0.10 у ступни
 *  из `LEG_HALFS`), а не «примерно ноль». Меряем-то мы ось, а на экране видно
 *  коробку: занизь зазор — и конечность ляжет серединой на пол, то есть
 *  наполовину утонет в нём. */
const LIMB_LIFT = 0.10;

const LIMB_ORDER = ['armL', 'armR', 'legL', 'legR'];

// ---- Лужа крови ----

/** Радиус лужи: случайный в этих пределах. Удвоен по решению владельца —
 *  первая версия (0.34…0.72) при росте тела 1.8 м читалась как пятнышко.
 *
 *  ⚠️ Это радиус ПЛОСКОСТИ, а не самой кляксы: на холсте она занимает
 *  примерно 0.56 полуширины (`base = N * 0.28` в `makeBloodTexture`) плюс
 *  брызги по краям. То есть видимое пятно почти вдвое меньше этих чисел —
 *  крутить их, глядя на экран, а не на метры. */
const POOL_MIN_R = 0.68, POOL_MAX_R = 1.44;

/** За сколько секунд лужа растекается до полного размера. */
const POOL_GROW = 2.6;

/** ⚠️ Кровь живёт ДОЛЬШЕ тела (LIFE = 10 с) — намеренно: пятно на полу
 *  остаётся следом боя и после того, как тело истлело. Тот, кто прибежал
 *  на звук, видит, что здесь кого-то убили. */
const POOL_LIFE = 15.0;
const POOL_FADE = 2.5;

/** Предел одновременных луж. Чуть больше предела тел (MAX): лужи переживают
 *  свои трупы, и на пределе их накапливается на пару штук больше. */
const POOL_MAX = 16;

const POOL_ALPHA = 0.92;

/** Сколько разных клякс держим в кэше. Форма случайная (см. `makeBloodTexture`),
 *  но рисовать холст на каждую смерть незачем: шести вариантов при случайном
 *  повороте и растяжении хватает, чтобы две лужи рядом не выглядели копиями. */
const POOL_VARIANTS = 6;

const UP = new THREE.Vector3(0, 1, 0);
const _axis = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _yawQ = new THREE.Quaternion();

/** Живые трупы. Обычный массив: их единицы, и порядок = возраст. */
const corpses = [];

/** Толчки из кадров OP_KILL, ещё не пригодившиеся: id жертвы → {dir, power, t}.
 *
 *  ⚠️ Зачем очередь, а не «родить труп прямо в OP_KILL». Кадр смерти приходит
 *  в РЕАЛЬНОМ времени, а тело нарисовано в `now − interpDelay` (150–400 мс
 *  назад). Родив труп по OP_KILL, мы поставили бы его туда, где игрок ещё
 *  только будет, — на спринте это метр с лишним, и смерть выглядела бы
 *  рывком вбок. Поэтому кадр только КЛАДЁТ импульс, а тело рождает снапшот,
 *  когда до момента смерти доедет интерполяция (см. remote.js). Тот же
 *  принцип, что у гранат: сетевые данные живут на своей временной оси. */
const pending = new Map();

/** Сколько ждём снапшот со смертью. Заметно больше любого разумного буфера
 *  интерполяции (потолок 400 мс), но не бесконечно: за стеной убитый в
 *  снапшот вообще не попадёт, и импульс так и останется невостребованным. */
const PENDING_TTL = 1500;

// Тело роняет снапшот, а не кадр смерти (см. `pending`). Подписываемся,
// а не импортируемся оттуда: встречный импорт замкнул бы цикл модулей.
onRemoteDeath(spawnCorpse);

/** Запомнить удар, убивший игрока. Зовётся из combat.js по кадру OP_KILL. */
export function rememberKill(victim, dir, power) {
  pending.set(victim, { dir, power, t: performance.now() });
}

/** Забрать и снять с очереди толчок для жертвы. */
function takePush(id) {
  const p = pending.get(id);
  if (!p) return null;
  pending.delete(id);
  return p;
}

/**
 * Уронить тело. Зовётся из remote.js в момент, когда интерполяция довезла
 * переход «жив → мёртв», то есть ровно там, где игрок был нарисован.
 *
 * @param id     id жертвы — по нему ищется импульс из OP_KILL
 * @param state  {x, y, z} ступней, eye, yaw, lean, held, npc — то, чем игрок
 *               был отрисован последним живым кадром
 * @param sure   смерть подтверждена сервером (в снапшоте приехала запись
 *               с погашенным F_ALIVE). Тогда тело роняем независимо от того,
 *               доехал ли кадр OP_KILL: без него нет только ВЕКТОРА толчка,
 *               а не самого факта смерти.
 *
 * ⚠️ Экспортируется ради СТЕНДА (`charpreview.js`, вид `#corpse`): игрового
 * пути мимо `onRemoteDeath` нет и быть не должно, но проверить, как труп
 * выглядит в человеческой модели, иначе нечем — вход в игру требует захвата
 * мыши, а без него не крутится игровой цикл (см. md/cheatsheet.md).
 */
export function spawnCorpse(id, state, sure = false) {
  // ⚠️ Импульс — уже не признак смерти, а только её направление и сила.
  //
  // Раньше он был единственным признаком: сервер переставал слать и мёртвого,
  // и спрятавшегося, поэтому «пришёл ли OP_KILL» и решало, ронять тело или
  // нет. Один потерянный пакет на unreliable-канале WebRTC — и убитый молча
  // исчезал (жалоба владельца). Теперь факт смерти приезжает потоком
  // снапшотов (`sure`), а очередь `pending` осталась ровно тем, чем должна
  // быть: подсказкой «откуда прилетело». Нет её — тело просто подкашивается
  // (COLLAPSE ниже), и это несравнимо лучше пропажи.
  const push = takePush(id);
  if (!push && !sure) return;

  // ⚠️ Тело держим ЦЕЛИКОМ (`body`), а не по кускам: `layoutBody` и `syncSkin`
  // читают с него ещё и риг с кожей (`rig`/`skin`), а риг у человеческой модели
  // СВОЙ — плечо ниже, рука короче (см. skinbody.js::SKIN_RIG). Растащи объект
  // по полям, как было, — модель встанет по коробочным пропорциям, и кисть
  // промахнётся мимо цевья.
  const body = buildBody(state.npc, true);
  const { group, parts, limbs, hands, materials } = body;

  // ⚠️ Труп всегда выпрямляется в ПОЛНЫЙ РОСТ, даже если убит сидя или лёжа,
  //    и наклон Q/E снимается (lean = 0). Сложенное в тесную позу тело,
  //    брошенное на пол, читается как «прилёг отдохнуть», а не как убитый;
  //    падающий во весь рост с раскинутыми конечностями — как труп. Раньше
  //    раскладка бралась из позы последнего живого кадра (`state.eye`/`state.lean`)
  //    ради «появился ровно там, где был», но для мёртвого поза значения не
  //    имеет — важно, чтобы он упал естественно.
  layoutBody(body, STAND_EYE, 0);
  showHeld(hands, state.held);
  // Оружие ставим на уровень пояса. Живому это делает `applyHands`, мёртвому
  // считать веса поз не из чего — а без явной постановки пивот остаётся
  // в нуле группы, то есть ствол уезжает трупу под ноги.
  restHands(hands, STAND_EYE);
  limpPose(body);
  scene.add(group);

  const rod = Math.max(0.4, STAND_EYE - SHOULDER_DROP);
  const bottom = new THREE.Vector3(state.x, state.y, state.z);
  const top = new THREE.Vector3(state.x, state.y + rod, state.z);

  const vTop = new THREE.Vector3();
  const vBottom = new THREE.Vector3();
  if (push && push.power > 0.001) {
    const k = push.power * KICK;
    vTop.set(push.dir[0], push.dir[1], push.dir[2]).multiplyScalar(k * TOP_SHARE);
    vTop.y += k * LIFT;
    // Ступням тоже нужен подброс, иначе они остаются приклеенными к полу и
    // жёсткая связь гасит полёт всего тела о собственные ноги.
    vBottom.set(push.dir[0], push.dir[1], push.dir[2]).multiplyScalar(k * BOTTOM_SHARE);
    vBottom.y += k * LIFT * 0.5;
  } else {
    // Направления нет — сгорел в молотове ИЛИ кадр OP_KILL до нас не доехал.
    // Подкашиваем вперёд по курсу (см. COLLAPSE).
    vTop.set(-Math.sin(state.yaw), 0, -Math.cos(state.yaw)).multiplyScalar(COLLAPSE);
  }

  corpses.push({
    group, parts, limbs, materials, body,
    top, bottom, vTop, vBottom, rod,
    yaw: state.yaw,
    age: 0,
    // Второй этап: тело улеглось → конечности доваливаются на пол, под телом
    // натекает лужа. `surfaceY` до остановки неизвестен (тело ещё летит),
    // `limbW` — угловые скорости четырёх конечностей, по одной на плечо/бедро.
    rest: false, settled: false, surfaceY: 0,
    limbW: new Float32Array(4),
  });
  // Свежий труп важнее старого: снимаем самый первый, он и так почти истлел.
  while (corpses.length > MAX) release(corpses.shift());
}

/** Обмякшая поза: руки и ноги раскинуты в РАЗНЫЕ стороны, каждая конечность —
 *  по-своему и случайно.
 *
 *  Раньше руки падали строго вдоль тела (обе одинаково, только по rotation.x),
 *  и труп читался как «прилёг», а не как упавший. Теперь и плечи, и бёдра
 *  получают ещё и случайный разворот ВБОК (rotation.z) — левая конечность
 *  наружу в −X, правая в +X, — а сгибы локтей и колен разные у каждой.
 *  Отсюда «руки-ноги в разные стороны».
 *
 *  Ставится один раз, в момент смерти, и дальше не меняется: конечности
 *  физики не имеют — падает шест, а руки и ноги едут вместе с ним. Плавно
 *  въезжать в эту позу незачем, смерть и есть переключение. Случайность
 *  берётся прямо тут (`Math.random`): труп чисто клиентский, раскладку
 *  синхронизировать не с кем. */
function limpPose(r) {
  const { armL, armR, legL, legR } = r.limbs;
  const rnd = (a, b) => a + Math.random() * (b - a);

  // Руки: плечо разворачивается наружу вбок (z), плюс случайный вынос
  // вперёд/назад (x); локоть подламывается по-своему. Диапазон широкий —
  // от «рука над головой» до «прижата к боку».
  armL.joints[0].rotation.x = rnd(-0.6, 0.9);
  armL.joints[0].rotation.z = -rnd(0.4, 1.3);
  armR.joints[0].rotation.x = rnd(-0.6, 0.9);
  armR.joints[0].rotation.z = rnd(0.4, 1.3);
  armL.joints[1].rotation.x = rnd(0.0, 1.0);
  armR.joints[1].rotation.x = rnd(0.0, 1.0);

  // Ноги: бедро тоже разворачивается наружу (z) на случайный угол, колено
  // гнётся только назад (отрицательный x, как в animateLimbs) и на свою долю.
  legL.joints[0].rotation.x = rnd(-0.2, 0.6);
  legL.joints[0].rotation.z = -rnd(0.1, 0.6);
  legR.joints[0].rotation.x = rnd(-0.2, 0.6);
  legR.joints[0].rotation.z = rnd(0.1, 0.6);
  legL.joints[1].rotation.x = -rnd(0.1, 0.8);
  legR.joints[1].rotation.x = -rnd(0.1, 0.8);
}

// ============================================================
//  Доваливание конечностей: тело легло — руки и ноги ложатся следом.
//
//  ⚠️ Почему это НЕ анимация «улечься», а решение задачи «где пол».
//  Тело падает как попало: на спину, на бок, лицом вниз, на ящик. Поза из
//  `limpPose` задана в системе координат тела, поэтому одна и та же рука
//  после разных падений оказывается то на полу, то в небе. Единственное,
//  что тут можно сделать честно, — каждый кадр смотреть, ГДЕ кисть сейчас,
//  и опускать её к поверхности. Тогда разброс сохраняется (углы остаются
//  разными), а торчащих вверх конечностей не остаётся ни при каком падении.
// ============================================================

const _wq = new THREE.Quaternion();
const _pq = new THREE.Quaternion();
const _delta = new THREE.Quaternion();
const _jp = new THREE.Vector3();
const _seg = new THREE.Vector3();

/** Контрольные точки конечности в системе плеча: локоть (колено), запястье
 *  (лодыжка) и КОНЕЦ кисти (ступни). Именно они, а не одна кисть, решают,
 *  где конечность коснётся пола, — см. `dropLimb`. */
const _pts = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];

const clamp1 = (v) => v < -1 ? -1 : v > 1 ? 1 : v;

function limbPoints(limb, origin) {
  limb.joints[1].getWorldPosition(_pts[0]);
  limb.joints[2].getWorldPosition(_pts[1]);
  // Конец последнего сегмента: его центр вдвое дальше своего начала.
  limb.meshes[2].getWorldPosition(_pts[2]);
  _pts[2].sub(_pts[1]).multiplyScalar(2).add(_pts[1]);
  for (const p of _pts) p.sub(origin);
}

/** Поворот всей конечности целиком в плече/бедре: опускаем её на поверхность.
 *
 *  ⚠️ Вращаем ЖЁСТКО всю цепочку, не трогая локоть и колено: сгиб из
 *  `limpPose` анатомически возможен, а «доведи каждый сустав до пола»
 *  вывернуло бы колено вперёд. Форма позы сохраняется, меняется только
 *  её наклон.
 *
 *  ⚠️ БАГ-УРОК: целиться одной КИСТЬЮ нельзя (жалоба владельца «конечности
 *  проваливаются под пол»). У согнутой руки самая низкая точка — локоть,
 *  а не кисть: плечо идёт вниз, предплечье возвращается вверх. Уложив кисть
 *  на пол, мы честно загоняли локоть под него. Поэтому угол считается по
 *  ВСЕМ контрольным точкам, а берётся МЕНЬШИЙ из них: конечность опускается
 *  ровно до первого касания, а точка, уже оказавшаяся под полом, даёт
 *  отрицательный угол и поднимает конечность обратно. */
function dropLimb(limb, c, i, dt) {
  const joint = limb.joints[0];
  joint.getWorldPosition(_jp);
  limbPoints(limb, _jp);

  // ⚠️ Потолок в ноль (не выше горизонта) — не перестраховка. `surfaceAt`
  // берёт опору под телом, но стоит ей однажды оказаться выше плеча, и
  // формула честно потянет руку ВВЕРХ к недостижимому полу — то есть вернёт
  // ровно тот торчащий в небо силуэт, ради которого всё это писалось.
  // Мёртвая конечность не поднимается над горизонтом ни при каком раскладе.
  const floorY = c.surfaceY + LIMB_LIFT;
  let d = Infinity, low = null;
  for (const p of _pts) {
    const len = p.length();
    if (len < 0.05) continue;
    const want = Math.min(0, Math.asin(clamp1((floorY - _jp.y) / len)));
    const dp = Math.asin(clamp1(p.y / len)) - want;    // >0 — точка выше пола
    if (dp < d) { d = dp; low = p; }
  }
  if (!low) return false;
  if (Math.abs(d) < 0.02) { c.limbW[i] = 0; return false; }

  // Падение с ускорением: конечность разгоняется и упирается в пол,
  // а не «доезжает» с постоянной скоростью.
  let w = c.limbW[i];
  if (w * d < 0) w = 0;                       // цель ушла в другую сторону
  w += Math.sign(d) * LIMB_FALL * dt;
  w = Math.max(-LIMB_MAX_W, Math.min(LIMB_MAX_W, w));
  let step = w * dt;
  if (Math.abs(step) >= Math.abs(d)) { step = d; w = 0; }   // легла — гасим
  c.limbW[i] = w;

  // Ось поворота — горизонтальная и поперёк КРИТИЧНОЙ точки (той, что первой
  // коснётся пола): так она едет вниз по своей вертикальной плоскости, а не
  // заносится вбок. Строго вертикальная конечность оси не задаёт
  // (вырождение) — роняем в произвольную сторону.
  _seg.set(low.z, 0, -low.x);
  const h = _seg.length();
  if (h < 1e-4) _seg.set(1, 0, 0); else _seg.divideScalar(h);

  _delta.setFromAxisAngle(_seg, step);
  joint.parent.getWorldQuaternion(_pq);
  joint.quaternion.premultiply(_pq).premultiply(_delta).premultiply(_pq.invert());
  return true;
}

/** Локоть и колено: если сегмент после падения тела смотрит вверх —
 *  распрямляем сустав.
 *
 *  ⚠️ Именно распрямляем (углы к нулю), а не доворачиваем в мире. Локоть
 *  и колено гнутся ровно в одну сторону; поворот «куда надо» вывернул бы
 *  сустав наизнанку. Распрямление же выводит сегмент на одну линию с плечом
 *  или бедром — а те уже уложены на пол `dropLimb`. */
function straightenJoint(joint, dt) {
  joint.getWorldQuaternion(_wq);
  _seg.set(0, -1, 0).applyQuaternion(_wq);
  if (_seg.y < 0.04) return false;                  // сегмент и так лежит
  const k = Math.max(0, 1 - LIMB_STRAIGHTEN * dt);
  joint.rotation.x *= k; joint.rotation.y *= k; joint.rotation.z *= k;
  return Math.abs(joint.rotation.x) + Math.abs(joint.rotation.z) > 0.02;
}

/** Шаг доваливания всех четырёх конечностей. Крутится только пока хоть одна
 *  движется: улёгшийся труп не должен стоить ни одного пересчёта матриц. */
function settleLimbs(c, dt) {
  // ⚠️ Обязательно: положение тела мы ТОЛЬКО ЧТО поставили в `orient`, а
  // матрицы мира пересчитывает рендер — то есть без этой строки мы читали бы
  // позиции кадровой давности и целились бы в пол там, где тело уже не лежит.
  c.group.updateMatrixWorld(true);
  let moving = false;
  for (let i = 0; i < LIMB_ORDER.length; i++) {
    const limb = c.limbs[LIMB_ORDER[i]];
    if (dropLimb(limb, c, i, dt)) moving = true;
    if (straightenJoint(limb.joints[1], dt)) moving = true;
  }
  if (!moving) c.settled = true;
}

// ============================================================
//  Лужа крови
//
//  Чистая косметика и целиком клиентская, как и само падение тела: на
//  сервере мёртвый неподвижен и ни на что не влияет. Лужа рождается не в
//  момент смерти, а когда тело УЛЕГЛОСЬ, — кровь натекает под лежащим,
//  а не под летящим.
// ============================================================

/** Готовые кляксы. Заполняется лениво, при первой смерти: холст рисуется
 *  один раз за сессию, а не на каждый труп. */
const bloodTex = [];

/** Случайная клякса на холсте: контур с гуляющим по углу радиусом плюс
 *  брызги по краям. Радиус гуляет суммой двух синусоид со случайной фазой —
 *  контур получается плавным и при этом каждый раз разным (ломаная из
 *  случайных точек выглядела бы как звезда). */
function makeBloodTexture() {
  const N = 128, cv = document.createElement('canvas');
  cv.width = cv.height = N;
  const g = cv.getContext('2d');
  const cx = N / 2, cy = N / 2, base = N * 0.28;

  // Мягкий край: без размытия пятно выглядит вырезанным ножницами.
  g.filter = 'blur(3px)';

  const a1 = Math.random() * 6.283, a2 = Math.random() * 6.283;
  const k1 = 2 + Math.floor(Math.random() * 2);
  const k2 = 4 + Math.floor(Math.random() * 3);
  g.beginPath();
  for (let i = 0; i <= 48; i++) {
    const t = (i / 48) * 6.283;
    const r = base * (1 + 0.24 * Math.sin(k1 * t + a1) + 0.12 * Math.sin(k2 * t + a2));
    const x = cx + Math.cos(t) * r, y = cy + Math.sin(t) * r;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
  const grad = g.createRadialGradient(cx, cy, base * 0.15, cx, cy, base * 1.25);
  grad.addColorStop(0, '#6e0d0d');       // серёдка гуще и темнее
  grad.addColorStop(1, '#2f0606');
  g.fillStyle = grad;
  g.fill();

  // Брызги: без них лужа читается как аккуратно положенная наклейка.
  g.fillStyle = '#4a0808';
  const drops = 5 + Math.floor(Math.random() * 5);
  for (let i = 0; i < drops; i++) {
    const t = Math.random() * 6.283;
    const d = base * (1.0 + Math.random() * 0.5);
    const rr = base * (0.05 + Math.random() * 0.12);
    g.beginPath();
    g.ellipse(cx + Math.cos(t) * d, cy + Math.sin(t) * d, rr, rr * (0.6 + Math.random() * 0.8), t, 0, 6.283);
    g.fill();
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;   // холст нарисован в sRGB
  return tex;
}

function pickBloodTex() {
  if (bloodTex.length < POOL_VARIANTS) bloodTex.push(makeBloodTexture());
  return bloodTex[Math.floor(Math.random() * bloodTex.length)];
}

/** Плоскость лужи. Геометрия одна на всех и заранее положена в плоскость XZ,
 *  поэтому размер задаётся масштабом по X и Z. */
const POOL_GEO = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);

const pools = [];

/** Поверхность под точкой: верх ящика, на котором лежит тело, иначе пол.
 *  Берём только те ящики, чей верх не выше самого тела, — иначе лужа уехала
 *  бы на крышу ящика, рядом с которым труп лежит на полу. */
function surfaceAt(x, z, y) {
  let g = 0;
  nearPoint(x, z, 0.01, (c) => {
    if (x > c.min.x && x < c.max.x && z > c.min.z && z < c.max.z
      && c.max.y > g && c.max.y <= y + 0.35) g = c.max.y;
  });
  return g;
}

function spawnPool(x, z, surfaceY) {
  const mat = new THREE.MeshBasicMaterial({
    map: pickBloodTex(), transparent: true, opacity: 0, depthWrite: false,
    polygonOffset: true, polygonOffsetFactor: -4,
  });
  const mesh = new THREE.Mesh(POOL_GEO, mat);
  // Чуть выше поверхности: вровень с полом плоскости дерутся за глубину.
  mesh.position.set(x, surfaceY + 0.012, z);
  mesh.rotation.y = Math.random() * 6.283;         // одна клякса — много видов
  mesh.scale.set(0.001, 1, 0.001);
  scene.add(mesh);
  pools.push({
    mesh, mat, age: 0,
    rx: (POOL_MIN_R + Math.random() * (POOL_MAX_R - POOL_MIN_R)) * (0.8 + Math.random() * 0.45),
    rz: (POOL_MIN_R + Math.random() * (POOL_MAX_R - POOL_MIN_R)) * (0.8 + Math.random() * 0.45),
  });
  while (pools.length > POOL_MAX) releasePool(pools.shift());
}

function releasePool(p) {
  scene.remove(p.mesh);
  p.mat.dispose();          // текстура общая (кэш) — её не трогаем
}

function updatePools(dt) {
  for (let i = pools.length - 1; i >= 0; i--) {
    const p = pools[i];
    p.age += dt;
    if (p.age >= POOL_LIFE) { releasePool(p); pools.splice(i, 1); continue; }

    // Растекание: быстро в начале, потом всё медленнее — так ведёт себя
    // натекающая жидкость, и так же читается «кровь идёт», а не «пятно возникло».
    const k = Math.min(1, p.age / POOL_GROW);
    const grow = 1 - (1 - k) * (1 - k);
    p.mesh.scale.set(2 * p.rx * grow, 1, 2 * p.rz * grow);

    const left = POOL_LIFE - p.age;
    p.mat.opacity = POOL_ALPHA * Math.min(1, p.age / 0.25) * (left < POOL_FADE ? left / POOL_FADE : 1);
  }
}

/** Один шаг падения точки.
 *
 *  ⚠️ Отскок от ящиков берётся ИЗ grenades.js, а не переписан здесь: выбор
 *  оси выталкивания — самая тонкая часть этого кода (баг-урок «граната
 *  проваливается в угол ящика у земли»), и второй копии у него быть не
 *  должно. Гранатные коэффициенты гасим своими — сразу после вызова. */
const _was = new THREE.Vector3();

function stepPoint(p, v, dt) {
  const lim = R - 0.5;
  v.y -= GRAVITY * dt;

  const air = Math.max(0, 1 - AIR_DAMP * dt);
  v.x *= air; v.z *= air;

  p.addScaledVector(v, dt);

  if (p.y < REST_Y) {
    p.y = REST_Y;
    v.y *= -BOUNCE;
    // Трение о пол — тоже по времени: лежащая точка проваливается под REST_Y
    // КАЖДЫЙ кадр (её туда тянет гравитация), и множитель на кадр остановил бы
    // скольжение за несколько кадров вместо метра проезда.
    const fr = Math.max(0, 1 - GROUND_FRICTION * dt);
    v.x *= fr; v.z *= fr;
  }
  if (p.x < -lim) { p.x = -lim; v.x *= -BOUNCE; }
  if (p.x > lim) { p.x = lim; v.x *= -BOUNCE; }
  if (p.z < -lim) { p.z = -lim; v.z *= -BOUNCE; }
  if (p.z > lim) { p.z = lim; v.z *= -BOUNCE; }

  // Гасим только ПО ФАКТУ удара о ящик, а не каждый кадр: сам `bounceAABB`
  // о столкновении не сообщает (возвращает лишь «легла на верх»), поэтому
  // ловим его по сдвигу точки.
  _was.copy(p);
  bounceAABB(p, v, POINT_R);
  if (!_was.equals(p)) v.multiplyScalar(HIT_DAMP);
}

/** Жёсткая связь между плечами и ступнями: обе точки подтягиваются к нужной
 *  длине навстречу друг другу. Это и делает из двух точек тело, а не две
 *  независимые песчинки. */
function constrain(c) {
  _axis.subVectors(c.top, c.bottom);
  const len = _axis.length();
  if (len < 1e-4) {
    // Точки схлопнулись (упор в угол) — расталкиваем вверх, иначе из нулевого
    // вектора не получить ориентацию и тело исчезнет в NaN.
    c.top.y = c.bottom.y + c.rod;
    return;
  }
  const fix = (len - c.rod) / len / 2;
  c.top.addScaledVector(_axis, -fix);
  c.bottom.addScaledVector(_axis, fix);
}

/** Ставит группу тела по двум точкам: начало — ступни, локальная ось Y — вдоль
 *  позвоночника. Курс сохраняется вращением вокруг самого позвоночника, иначе
 *  упавшее тело разворачивало бы лицом куда попало. */
function orient(c) {
  _axis.subVectors(c.top, c.bottom).normalize();
  _q.setFromUnitVectors(UP, _axis);
  _yawQ.setFromAxisAngle(UP, c.yaw);
  c.group.position.copy(c.bottom);
  c.group.quaternion.copy(_q).multiply(_yawQ);
}

function release(c) {
  scene.remove(c.group);
  // ⚠️ Обязательно, даже если модель так и не появилась: пока GLB летит по
  // сети, кожа лежит в очереди ожидания внутри `skinbody.js`, и не снятая
  // оттуда запись пережила бы и труп, и его группу (утечка + подмена коробок
  // на теле, которого уже нет в сцене).
  dropSkin(c.body.skin);
  // Материалы у трупа СВОИ (клоны) — освобождать обязательно, иначе каждая
  // смерть навсегда оставляла бы в памяти по шесть материалов. Геометрия
  // общая (UNIT_BOX и прочее), её не трогаем.
  for (const m of c.materials) m.dispose();
}

/** Шаг всех трупов и луж. Зовётся из главного цикла. */
export function updateCorpses(dt) {
  // Лужи живут дольше тел, поэтому шагают отдельным списком.
  if (pools.length) updatePools(dt);

  // Протухшие импульсы: жертву могло не быть видно вовсе (PVS), и тогда
  // тела не будет никогда — держать запись смысла нет.
  if (pending.size) {
    const now = performance.now();
    for (const [id, p] of pending) {
      if (now - p.t > PENDING_TTL) pending.delete(id);
    }
  }

  for (let i = corpses.length - 1; i >= 0; i--) {
    const c = corpses[i];
    c.age += dt;
    if (c.age >= LIFE) {
      release(c);
      corpses.splice(i, 1);
      continue;
    }

    stepPoint(c.top, c.vTop, dt);
    stepPoint(c.bottom, c.vBottom, dt);
    constrain(c);
    orient(c);

    // Тело остановилось: под ним натекает кровь, конечности доваливаются на пол.
    // Запас по возрасту нужен потому, что первые кадры тело ещё не разогналось,
    // а брошенное без импульса (сгорел в молотове) медленно и вовсе.
    if (!c.rest && c.age > 0.25
      && (c.vTop.lengthSq() + c.vBottom.lengthSq() < REST_SPEED2 || c.age > REST_TIMEOUT)) {
      c.rest = true;
      const px = (c.top.x + c.bottom.x) / 2, pz = (c.top.z + c.bottom.z) / 2;
      c.surfaceY = surfaceAt(px, pz, Math.min(c.top.y, c.bottom.y));
      spawnPool(px, pz, c.surfaceY);
    }
    if (c.rest && !c.settled) settleLimbs(c, dt);

    // ---- Человеческая модель повторяет позу рига костями ----
    //
    // Тем же `syncSkin`, что и у живого: физика трупа крутит те же шарниры,
    // а он читает НАПРАВЛЕНИЯ их сегментов — кто их повернул, ему всё равно.
    //
    // ⚠️ Каждый кадр, а не один раз в `spawnCorpse`, и это не расточительство.
    // Причин две. Первая: GLB может приехать уже ПОСЛЕ смерти — тело родится
    // коробочным, а кожа подменит его на месте (`makeSkin`), и без вызова
    // в кадре над трупом встала бы модель в позе привязки, то есть стоящий
    // навытяжку человек поверх лежащего. Вторая: доваливание конечностей
    // длится секунды, и его надо повторять.
    //
    // Поворот и положение самой группы (`orient`) при этом не в счёт: кости
    // ставятся в системе родителя, поэтому от того, как повёрнуто тело,
    // локальные повороты не зависят вовсе.
    if (c.body.skin) syncSkin(c.body);

    // Растворение — только в конце жизни, чтобы тело не «мигало» сразу.
    const left = LIFE - c.age;
    if (left < FADE) {
      const o = left / FADE;
      for (const m of c.materials) m.opacity = o;
    }
  }
}

/** Убрать все тела разом — на переподключении и смене матча. Иначе после
 *  обрыва связи в сцене остались бы висеть чужие трупы: их удаляет время,
 *  но десять секунд неправды на экране лучше не оставлять. */
export function clearCorpses() {
  for (const c of corpses) release(c);
  corpses.length = 0;
  for (const p of pools) releasePool(p);
  pools.length = 0;
  pending.clear();
}
